import React, { useReducer, useRef, useEffect } from "react";
import SceneGUI from "./scene.gui.tsx";
import Toast from "./toast.gui.tsx";
import EmptyState from "./emptyState.gui.tsx";
import ShortcutsModal from "./shortcuts.gui.tsx";
import SaveLoadModal from "./saveLoadModal.gui.tsx";
import ExportModal from "./exportModal.gui.tsx";
import SettingsModal from "./settings.gui.tsx";
import Scene from "../Scene.ts";

const LOCALSTORAGE_KEY = "nanimate-projects";

const initialState = {
  projects: JSON.parse(localStorage.getItem(LOCALSTORAGE_KEY) || "[]"),
  playing: false,
  scene: null,
  layers: null,
  lastUpdate: Date.now(),
  toasts: [],
  showShortcuts: false,
  showSaveModal: false,
  showLoadModal: false,
  showExportModal: false,
  showSettings: false,
  onionSkinsEnabled: true,
  onionSkinsBefore: 2,
  onionSkinsAfter: 2,
};

function reducer(state, action) {
  console.log(state);
  switch (action.type) {
    case "mounted":
      return { ...state, scene: new Scene(action.payload) };
      break;
    case "layerupdate":
      return { ...state, lastUpdate: Date.now() };
    case "show_export":
      return { ...state, showExportModal: true };
    case "hide_export":
      return { ...state, showExportModal: false };
    case "export":
      document.activeElement.blur();
      state.scene.exportScene(); // to gif in new tab
      return {
        ...state,
        showExportModal: false,
        toasts: [
          ...state.toasts,
          { id: Date.now(), message: "Exporting GIF...", type: "info" },
        ],
      };
    case "show_load":
      return { ...state, showLoadModal: true };
    case "hide_load":
      return { ...state, showLoadModal: false };
    case "load":
      state?.scene?.destroy();
      document.activeElement.blur();
      const newScene = new Scene(action.payload, action.project.data);
      return {
        ...state,
        scene: newScene,
        showLoadModal: false,
        toasts: [
          ...(state?.toasts ?? []),
          {
            id: Date.now(),
            message: `Loaded "${action.project.name}"!`,
            type: "success",
          },
        ],
      };
    case "show_save":
      return { ...state, showSaveModal: true };
    case "hide_save":
      return { ...state, showSaveModal: false };
    case "save":
      document.activeElement.blur();
      const sceneData = state.scene.save();
      const now = Date.now();

      const existingIndex = state.projects.findIndex(
        (p) => p._id === state.scene.id,
      );

      const projectData = {
        _id: state.scene.id,
        name: action.name,
        createdAt:
          existingIndex > -1 ? state.projects[existingIndex].createdAt : now,
        updatedAt: now,
        data: sceneData,
      };

      if (existingIndex > -1) {
        state.projects.splice(existingIndex, 1, projectData);
      } else {
        state.projects.push(projectData);
      }

      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(state.projects));
      return {
        ...state,
        showSaveModal: false,
        toasts: [
          ...state.toasts,
          {
            id: Date.now(),
            message: `Saved "${action.name}"!`,
            type: "success",
          },
        ],
      };
    case "dismiss_toast":
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.id),
      };
    case "show_shortcuts":
      return { ...state, showShortcuts: true };
    case "hide_shortcuts":
      return { ...state, showShortcuts: false };
    case "show_settings":
      return { ...state, showSettings: true };
    case "hide_settings":
      return { ...state, showSettings: false };
    case "toggle_onion_skins":
      if (state.scene) {
        state.scene.layers.forEach((layer) => {
          action.enabled ? layer.showSkins() : layer.hideSkins();
        });
      }
      return { ...state, onionSkinsEnabled: action.enabled };
    case "set_onion_skins_before":
      if (state.scene) {
        state.scene.layers.forEach((layer) => {
          layer.skins = [-action.count, layer.skins[1]];
        });
      }
      return { ...state, onionSkinsBefore: action.count };
    case "set_onion_skins_after":
      if (state.scene) {
        state.scene.layers.forEach((layer) => {
          layer.skins = [layer.skins[0], action.count];
        });
      }
      return { ...state, onionSkinsAfter: action.count };
  }
}

export default function GUIMain() {
  const containerRef = useRef(null);
  const [state, dispatch] = useReducer(reducer, initialState);

  const { scene, lastUpdate } = state;

  useEffect(() => {
    if (scene) {
      if (
        !scene.onlayerupdate &&
        !scene.onnextframe &&
        !scene.onprevframe &&
        !scene.onlayeradd
      ) {
        scene.onlayerremove =
          scene.onlayerduplicate =
          scene.onlayeractivate =
          scene.onlayerupdate =
          scene.onnextframe =
          scene.onprevframe =
          scene.onlayeradd =
            () => dispatch({ type: "layerupdate" });
      }
      return;
    }

    dispatch({ type: "mounted", payload: containerRef.current });
  });

  // Check if there's any content in any layer
  const hasContent =
    scene &&
    scene.layers &&
    scene.layers.some((layer) =>
      layer.frames.some((frame) => frame.paths && frame.paths.length > 0),
    );

  return (
    <>
      <SceneGUI
        playing={scene && scene.playing}
        togglePlay={() => scene.play()}
        lastUpdate={lastUpdate}
        containerRef={containerRef}
        activeLayer={scene && scene.layers[scene.active_layer]}
        layers={(scene && scene.layers) || []}
        onLoad={() => dispatch({ type: "show_load" })}
        onSave={() => dispatch({ type: "show_save" })}
        onExport={() => dispatch({ type: "show_export" })}
        onShowShortcuts={() => dispatch({ type: "show_shortcuts" })}
        onShowSettings={() => dispatch({ type: "show_settings" })}
        layerActions={{
          onSetLayerFPS: (fps, layer) => scene.setLayerFPS(fps, layer),
          onSetLayerColor: (color) => scene.setLayerColor(color),
          onSetLayerStrokeWidth: (width) => scene.setLayerStrokeWidth(width),
          onToggleLayerFill: (layer) => scene.toggleLayerFill(),
          onSelectLayer: (layer) => scene.activateLayer(layer),
          onAddLayer: () => scene.addLayer(),
          onAddFrame: () => scene.addFrame(),
          onRemoveFrame: () => scene.removeFrame(),
          onRemoveLayer: () => scene.removeLayer(),
          onDuplicateLayer: () => scene.duplicateLayer(),
          onMoveLayer: (layer, targetIndex) =>
            scene.moveLayer(layer, targetIndex),
          onUndo: () => scene.undo(),
          onRedo: () => scene.redo(),
        }}
      />
      <div className="toast-container">
        {state.toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => dispatch({ type: "dismiss_toast", id: toast.id })}
          />
        ))}
      </div>
      {!hasContent && (
        <EmptyState
          onShowShortcuts={() => dispatch({ type: "show_shortcuts" })}
        />
      )}
      {state.showShortcuts && (
        <ShortcutsModal onClose={() => dispatch({ type: "hide_shortcuts" })} />
      )}
      {state.showSaveModal && (
        <SaveLoadModal
          mode="save"
          currentProjectId={scene?.id}
          onSave={(name) => dispatch({ type: "save", name })}
          onLoad={() => {}}
          onClose={() => dispatch({ type: "hide_save" })}
          onShowToast={(message, type) =>
            dispatch({ type: "show_toast", message, toastType: type })
          }
        />
      )}
      {state.showLoadModal && (
        <SaveLoadModal
          mode="load"
          onSave={() => {}}
          onLoad={(project) =>
            dispatch({ type: "load", payload: containerRef.current, project })
          }
          onClose={() => dispatch({ type: "hide_load" })}
          onShowToast={(message, type) =>
            dispatch({ type: "show_toast", message, toastType: type })
          }
        />
      )}
      {state.showExportModal && scene && (
        <ExportModal
          scene={scene}
          onClose={() => dispatch({ type: "hide_export" })}
        />
      )}
      {state.showSettings && (
        <SettingsModal
          onionSkinsEnabled={state.onionSkinsEnabled}
          onionSkinsBefore={state.onionSkinsBefore}
          onionSkinsAfter={state.onionSkinsAfter}
          onToggleOnionSkins={(enabled) =>
            dispatch({ type: "toggle_onion_skins", enabled })
          }
          onSetOnionSkinsBefore={(count) =>
            dispatch({ type: "set_onion_skins_before", count })
          }
          onSetOnionSkinsAfter={(count) =>
            dispatch({ type: "set_onion_skins_after", count })
          }
          onClose={() => dispatch({ type: "hide_settings" })}
        />
      )}
    </>
  );
}
